(() => {
  if (window.SwitzerNewsletterPopupLoaded) return;
  window.SwitzerNewsletterPopupLoaded = true;

  const STORAGE_DISMISSED = 'sk_newsletter_popup_dismissed_until';
  const STORAGE_SUBSCRIBED = 'sk_newsletter_popup_subscribed';
  const timers = new WeakMap();

  const safeGet = (key) => {
    try { return window.localStorage.getItem(key); } catch (error) { return null; }
  };

  const safeSet = (key, value) => {
    try { window.localStorage.setItem(key, value); } catch (error) {}
  };

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  function shouldForceOpen(root) {
    const params = new URLSearchParams(window.location.search);
    if (params.get('show_popup') === '1') return true;
    return root.dataset.designMode === 'true' && root.dataset.previewInEditor === 'true';
  }

  function isEligibleDevice(root) {
    if (isMobile()) return root.dataset.showMobile !== 'false';
    return root.dataset.showDesktop !== 'false';
  }

  function openPopup(root, focus = true) {
    if (!root || !isEligibleDevice(root)) return;
    root.dataset.open = 'true';
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sk-newsletter-popup-open');

    if (focus) {
      window.setTimeout(() => {
        const field = root.querySelector('input[type="email"]');
        const close = root.querySelector('[data-popup-close]');
        (field || close)?.focus({ preventScroll: true });
      }, 60);
    }
  }

  function closePopup(root, remember = true) {
    if (!root) return;
    root.dataset.open = 'false';
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sk-newsletter-popup-open');

    const preview = shouldForceOpen(root);
    if (remember && !preview) {
      const days = Number(root.dataset.repeatDays || 7);
      const until = Date.now() + (days * 24 * 60 * 60 * 1000);
      safeSet(STORAGE_DISMISSED, String(until));
    }
  }

  function schedulePopup(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const success = Boolean(root.querySelector('[data-popup-success]'));
    if (success) {
      safeSet(STORAGE_SUBSCRIBED, 'true');
      openPopup(root, false);
      return;
    }

    if (shouldForceOpen(root)) {
      openPopup(root, false);
      return;
    }

    if (!isEligibleDevice(root)) return;
    if (safeGet(STORAGE_SUBSCRIBED) === 'true') return;

    const dismissedUntil = Number(safeGet(STORAGE_DISMISSED) || 0);
    if (dismissedUntil > Date.now()) return;

    const delay = Math.max(0, Number(root.dataset.delaySeconds || 5)) * 1000;
    const timer = window.setTimeout(() => openPopup(root), delay);
    timers.set(root, timer);
  }

  function init(root) {
    if (!root) return;
    schedulePopup(root);

    const shopButton = root.querySelector('[data-shop-discount]');
    if (shopButton && shopButton.dataset.popupBound !== 'true') {
      shopButton.dataset.popupBound = 'true';
      shopButton.addEventListener('click', async (event) => {
        const inThemeEditor = root.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);

        event.preventDefault();
        event.stopPropagation();

        const original = shopButton.textContent;

        if (inThemeEditor) {
          shopButton.textContent = 'Use Preview to test 15% off';
          shopButton.setAttribute('aria-label', 'Discount application is disabled inside the theme editor. Open the normal theme Preview to test it.');

          window.setTimeout(() => {
            shopButton.textContent = original;
            shopButton.removeAttribute('aria-label');
          }, 2800);
          return;
        }

        if (shopButton.dataset.applyingDiscount === 'true') return;
        shopButton.dataset.applyingDiscount = 'true';
        shopButton.setAttribute('aria-busy', 'true');

        // Keep the original success-screen experience: clicking the shop button
        // closes the popup immediately and silently applies the discount to the
        // shopper's current Shopify session without navigating or changing scroll.
        closePopup(root, false);
        safeSet(STORAGE_SUBSCRIBED, 'true');

        try {
          const response = await fetch(shopButton.href, {
            method: 'GET',
            credentials: 'same-origin',
            redirect: 'follow',
            cache: 'no-store',
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (!response.ok) throw new Error(`Discount request failed with status ${response.status}`);
        } catch (error) {
          // The customer can still use the visible Copy Code option if their
          // browser blocks the background request. Do not navigate or change
          // their current scroll position.
          console.warn('Switzer newsletter discount could not be applied in the background.', error);
        } finally {
          shopButton.textContent = original;
          shopButton.removeAttribute('aria-busy');
          delete shopButton.dataset.applyingDiscount;
        }
      });
    }

    const copy = root.querySelector('[data-copy-code]');
    if (copy && copy.dataset.popupBound !== 'true') {
      copy.dataset.popupBound = 'true';
      copy.addEventListener('click', async () => {
        const code = root.querySelector('[data-discount-code]')?.textContent?.trim();
        if (!code) return;
        const original = copy.textContent;
        try {
          await navigator.clipboard.writeText(code);
          copy.textContent = 'Copied!';
        } catch (error) {
          const textarea = document.createElement('textarea');
          textarea.value = code;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          textarea.remove();
          copy.textContent = 'Copied!';
        }
        window.setTimeout(() => { copy.textContent = original; }, 1800);
      });
    }
  }

  function initAll(scope = document) {
    scope.querySelectorAll?.('[data-newsletter-popup]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());

  // Use one delegated close handler so the X and overlay keep working even
  // after Shopify dynamically re-renders this section in the theme editor.
  // Capture phase also prevents another storefront layer from swallowing the click.
  document.addEventListener('click', (event) => {
    const closeTrigger = event.target.closest?.('[data-popup-close]');
    if (!closeTrigger) return;

    const root = closeTrigger.closest('[data-newsletter-popup]');
    if (!root) return;

    event.preventDefault();
    event.stopPropagation();
    closePopup(root);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-newsletter-popup][data-open="true"]').forEach((root) => closePopup(root));
  });

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.querySelector?.('[data-newsletter-popup]') || event.target.matches?.('[data-newsletter-popup]') && event.target;
    if (root) {
      root.dataset.initialized = 'true';
      openPopup(root, false);
    }
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const root = event.target.querySelector?.('[data-newsletter-popup]') || event.target.matches?.('[data-newsletter-popup]') && event.target;
    if (root) closePopup(root, false);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.('[data-newsletter-popup]') || event.target.matches?.('[data-newsletter-popup]') && event.target;
    if (root && timers.has(root)) {
      window.clearTimeout(timers.get(root));
      timers.delete(root);
    }
  });
})();
